import React from 'react';
import { motion } from 'framer-motion';
import { Target, ShieldCheck, Users, Sparkles, Bot, Video } from 'lucide-react';

const WhatItIsSection = () => {
  const features = [
    {
      icon: Target,
      title: 'QUALITY-FIRST SCORING',
      description: 'Your submissions are scored based on clarity, completeness, and usefulness for AI training.',
      color: 'text-neon-pink',
    },
    {
      icon: ShieldCheck,
      title: 'VERIFIED CONTRIBUTORS',
      description: 'Side Questers go through identity and environment verification for trusted data collection.',
      color: 'text-success-green',
    },
    {
      icon: Users,
      title: 'ETHICAL DATA COLLECTION',
      description: 'Structured, fair, and transparent data collection that respects privacy and contributors.',
      color: 'text-trophy-gold',
    },
  ];

  return (
    <section id="what-it-is" className="py-20 px-4 sm:px-6 lg:px-8 relative">
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-neon-pink/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-trophy-gold/30 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-neon-pink" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-foreground">
              WHAT IT IS
            </h2>
            <Sparkles className="w-6 h-6 text-neon-pink" />
          </div>
        </motion.div>

        {/* Main Description Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg p-6 sm:p-8 md:p-10 mb-12 relative overflow-hidden"
        >
          {/* Decorative icons */}
          <motion.div
            className="absolute top-4 right-4 opacity-20"
            animate={{ rotate: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Bot className="w-16 h-16 text-neon-pink" />
          </motion.div>
          <motion.div
            className="absolute bottom-4 left-4 opacity-20"
            animate={{ rotate: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            <Video className="w-12 h-12 text-trophy-gold" />
          </motion.div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h3 className="font-pixel text-sm sm:text-base md:text-lg text-trophy-gold mb-6">
              Singularity Labs
            </h3>
            <p className="font-terminal text-xl sm:text-2xl md:text-3xl text-foreground leading-relaxed mb-4">
              A <span className="text-neon-pink">gamified task-based human data collection platform</span> where Side Questers
              earn <span className="text-trophy-gold">cash rewards</span> by submitting clear, real-world video data
              from everyday tasks used to train <span className="text-success-green">AI and robotics</span>.
            </p>
          </div>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="arcade-panel rounded-lg p-6 text-center group hover:translate-y-[-4px] transition-transform"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded bg-deep-purple border-2 border-dark-border mb-4 group-hover:shadow-glow-pink transition-shadow`}>
                <feature.icon className={`w-8 h-8 ${feature.color}`} />
              </div>
              <h4 className="font-pixel text-[10px] sm:text-xs text-foreground mb-3">
                {feature.title}
              </h4>
              <p className="font-terminal text-lg text-lavender">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatItIsSection;
